import {
  type ShapeTextRange,
  paragraphRangeAt,
  visualRowRangeAt,
  wordRangeAt,
} from '@/lib/shapes/shape-text-navigation'
import type { ClickSelectionGranularity } from './types/click-selection-granularity'
import type { MultiClickState } from './types/multi-click-state'
import type { MultiClickInput } from './types/multi-click-input'
import type { ClickSelectionRangeArgs } from './types/click-selection-range-args'

/**
 * Down-to-down window for one sequence. 500ms is the Windows `SPI_GETDOUBLECLICKTIME` default and
 * Chrome's own interval, so it matches the muscle memory of whoever is testing this. The repo
 * already keeps a 500ms sibling in `PHOTO_TOUCH_LONG_PRESS_MS`.
 */
export const MULTI_CLICK_INTERVAL_MS = 500

/**
 * How far the pointer may wander from the sequence anchor and still count as the same click, as a
 * Chebyshev distance in **client** pixels. Matches Windows' `SM_CXDOUBLECLK` and the repo's own
 * `PHOTO_DRAG_THRESHOLD_SCREEN_PX`.
 *
 * Deliberately NOT divided by `canvasZoom`: client coordinates are already viewport pixels, so this
 * is screen-space by construction, which is what we want — pointing precision belongs to the hand,
 * not to the document. At 25% zoom the same 4px wiggle covers 16 document px and must still read as
 * one click. This is the exact inverse of `clientPointToShapeLocal`, which *must* divide by zoom
 * because it maps the other way, into document space.
 */
export const MULTI_CLICK_SLOP_SCREEN_PX = 4

/** Top of the ladder. A further rapid click clamps here rather than wrapping — see `nextMultiClickState`. */
export const MAX_CLICK_GRANULARITY = 5

/**
 * Advance a click sequence, or `null` when the press kills it.
 *
 * A non-primary button ends the sequence outright, so a right-click cannot be used to continue a
 * ladder. Otherwise the count restarts at 1 whenever the sequence has lapsed, moved off its anchor,
 * or turned into a drag.
 */
export function nextMultiClickState(
  prev: MultiClickState | null,
  input: MultiClickInput
): MultiClickState | null {
  if (input.button !== 0) return null

  const fresh: MultiClickState = {
    count: 1,
    time: input.time,
    clientX: input.clientX,
    clientY: input.clientY,
    dragged: false,
  }

  if (!prev || prev.dragged) return fresh
  if (input.time - prev.time > MULTI_CLICK_INTERVAL_MS) return fresh
  if (
    Math.max(Math.abs(input.clientX - prev.clientX), Math.abs(input.clientY - prev.clientY)) >
    MULTI_CLICK_SLOP_SCREEN_PX
  ) {
    return fresh
  }

  return {
    // Clamped, not wrapped. Wrapping back to a caret would silently drop a whole-frame selection —
    // which reads as "my selection vanished" and destroys text if the user then types. Chrome clamps
    // at its own top tier for the same reason. `time` still advances so the sequence cannot decay
    // while the user keeps clicking.
    count: Math.min(prev.count + 1, MAX_CLICK_GRANULARITY),
    time: input.time,
    clientX: prev.clientX,
    clientY: prev.clientY,
    dragged: false,
  }
}

/** True once the pointer has wandered off `state`'s anchor by more than the slop. */
export function isBeyondClickSlop(
  state: MultiClickState,
  clientX: number,
  clientY: number
): boolean {
  return (
    Math.max(Math.abs(clientX - state.clientX), Math.abs(clientY - state.clientY)) >
    MULTI_CLICK_SLOP_SCREEN_PX
  )
}

export function granularityForCount(count: number): ClickSelectionGranularity {
  if (count <= 1) return 'caret'
  if (count === 2) return 'word'
  if (count === 3) return 'line'
  if (count === 4) return 'paragraph'

  return 'all'
}

/** Frame-local half-open range for one granularity. */
export function resolveClickSelectionRange({
  granularity,
  text,
  offset,
  carets,
  consumedLength,
}: ClickSelectionRangeArgs): ShapeTextRange {
  switch (granularity) {
    case 'word':
      return wordRangeAt(text, offset)
    case 'line':
      return visualRowRangeAt(carets, text, offset, consumedLength)
    case 'paragraph':
      return paragraphRangeAt(text, offset)
    case 'all':
      // The whole frame, including any tail the flow could not lay out. The highlight stops at the
      // last laid-out row by itself (`computeSelectionRects` has no carets past `consumedLength`
      // that span any width), while copy/cut/replace act on everything the frame holds — which is
      // what the overflow badge already promises the user is there.
      return { start: 0, end: text.length }
    case 'caret':
    default:
      return { start: offset, end: offset }
  }
}
